import { Box, Button, Flex, FormControl, Input, InputGroup, InputRightElement, chakra } from '@chakra-ui/react';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import type { ContractAbiItemInput } from '../types';

import { HOUR, SECOND } from 'lib/consts';
import { useI18n } from 'lib/i18n/context';
import ClearButton from 'ui/shared/ClearButton';

import { ADDRESS_CONTRACT_KEYS } from '../../../i18n';
import ContractMethodAddressInputField from './ContractMethodAddressInputField';
import ContractMethodFieldLabel from './ContractMethodFieldLabel';
import ContractMethodFieldRow from './ContractMethodFieldRow';
import {
  CONTRACT_METHOD_FIELD_INPUT_HEIGHT,
  CONTRACT_METHOD_FIELD_ROW_GAP,
  useContractMethodFieldStyles,
} from './contractMethodFieldStyles';
import ContractMethodMultiplyButton from './ContractMethodMultiplyButton';
import useFormatFieldValue from './useFormatFieldValue';
import useValidateField from './useValidateField';
import { matchInt } from './utils';

const TIMESTAMP_BUTTON_REGEXP = /time|deadline|expiration|expiry/i;

interface Props {
  data: ContractAbiItemInput;
  hideLabel?: boolean;
  path: string;
  className?: string;
  isDisabled: boolean;
  isOptional?: boolean;
  level: number;
  px?: number;
}

const ContractMethodFieldInput = ({ data, hideLabel, path: name, className, isDisabled, isOptional: isOptionalProp, level, px }: Props) => {
  const { t } = useI18n();
  const ref = React.useRef<HTMLInputElement>(null);
  const styles = useContractMethodFieldStyles();
  const isTopLevel = level === 0;

  const isNativeCoin = data.fieldType === 'native_coin';
  const isOptional = isOptionalProp || isNativeCoin;
  const isAddressField = data.type === 'address';

  const argTypeMatchInt = React.useMemo(() => matchInt(data.type), [ data.type ]);
  const hasTimestampButton = React.useMemo(() => TIMESTAMP_BUTTON_REGEXP.test(data.name || ''), [ data.name ]);
  const validate = useValidateField({ isOptional, argType: data.type, argTypeMatchInt });
  const format = useFormatFieldValue({ argType: data.type, argTypeMatchInt });

  const { control, setValue, getValues } = useFormContext();
  const { field, fieldState } = useController({ control, name, rules: { validate } });

  const hasMultiplyButton = argTypeMatchInt && Number(argTypeMatchInt.power) >= 64;

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = format(event.target.value);
    field.onChange(formattedValue);
    setValue(name, formattedValue);
  }, [ field, name, setValue, format ]);

  const handleClear = React.useCallback(() => {
    setValue(name, '');
    ref.current?.focus();
  }, [ name, setValue ]);

  const handleMultiplyButtonClick = React.useCallback((power: number) => {
    const zeroes = Array(power).fill('0').join('');
    const value = getValues(name);
    const newValue = format(value ? value + zeroes : '1' + zeroes);
    setValue(name, newValue, { shouldValidate: true });
  }, [ format, getValues, name, setValue ]);

  const handleMaxIntButtonClick = React.useCallback(() => {
    if (!argTypeMatchInt) {
      return;
    }

    const newValue = format(argTypeMatchInt.max.toString());
    setValue(name, newValue, { shouldValidate: true });
  }, [ format, name, setValue, argTypeMatchInt ]);

  const handleTimestampButtonClick = React.useCallback(() => {
    const newValue = format(String(Math.floor((Date.now() + HOUR) / SECOND)));
    setValue(name, newValue, { shouldValidate: true });
  }, [ format, name, setValue ]);

  const error = fieldState.error;

  const inputProps = {
    h: isTopLevel ? CONTRACT_METHOD_FIELD_INPUT_HEIGHT : undefined,
    minH: isTopLevel ? CONTRACT_METHOD_FIELD_INPUT_HEIGHT : undefined,
    fontSize: isTopLevel ? '12px' : undefined,
    lineHeight: isTopLevel ? '24px' : undefined,
    bgColor: styles.inputBg,
    border: '1px solid',
    borderColor: styles.inputBorder,
    borderRadius: isTopLevel ? '4px' : 'base',
    px: isTopLevel ? 2 : undefined,
    color: `${ styles.inputText } !important`,
    _placeholder: { color: styles.inputPlaceholder },
    _focusVisible: isTopLevel ? { borderColor: styles.inputBorder, boxShadow: 'none' } : undefined,
  };

  const inputControl = isAddressField && isTopLevel ? (
    <ContractMethodAddressInputField
      data={ data }
      path={ name }
      isDisabled={ isDisabled }
      isOptional={ isOptional }
    />
  ) : (
    <FormControl isDisabled={ isDisabled }>
      <InputGroup size="xs">
        <Input
          { ...field }
          { ...(argTypeMatchInt ? {
            as: NumericFormat,
            thousandSeparator: ' ',
            decimalScale: 0,
            allowNegative: !argTypeMatchInt.isUnsigned,
          } : {}) }
          ref={ ref }
          onChange={ handleChange }
          required={ !isOptional }
          isInvalid={ Boolean(error) }
          placeholder={ data.type }
          autoComplete="off"
          data-1p-ignore
          paddingRight={ hasMultiplyButton ? '120px' : '40px' }
          { ...inputProps }
        />
        <InputRightElement
          w="auto"
          right={ 1 }
          bgColor={ isTopLevel ? styles.inputBg : undefined }
          h={ isTopLevel ? CONTRACT_METHOD_FIELD_INPUT_HEIGHT : 'calc(100% - 4px)' }
          top={ isTopLevel ? 0 : '2px' }
          borderRadius="base"
        >
          { field.value !== undefined && field.value !== '' && <ClearButton onClick={ handleClear } isDisabled={ isDisabled }/> }
          { argTypeMatchInt && !isNativeCoin && (hasTimestampButton ? (
            <Button
              variant="subtle"
              colorScheme="gray"
              size="xs"
              fontSize="normal"
              fontWeight={ 500 }
              ml={ 1 }
              onClick={ handleTimestampButtonClick }
              isDisabled={ isDisabled }
            >
              { t(ADDRESS_CONTRACT_KEYS.actions.nowPlusOneHour) }
            </Button>
          ) : (
            <Button
              variant="subtle"
              colorScheme="gray"
              size="xs"
              fontSize="normal"
              fontWeight={ 500 }
              ml={ 1 }
              onClick={ handleMaxIntButtonClick }
              isDisabled={ isDisabled }
            >
              { t(ADDRESS_CONTRACT_KEYS.actions.max) }
            </Button>
          )) }
          { hasMultiplyButton && <ContractMethodMultiplyButton onClick={ handleMultiplyButtonClick } isDisabled={ isDisabled }/> }
        </InputRightElement>
      </InputGroup>
      { error && <Box color="error" fontSize="sm" lineHeight={ 5 } mt={ 1 }>{ error.message }</Box> }
    </FormControl>
  );

  if (hideLabel) {
    return (
      <Box className={ className } w="100%" px={ px ?? 0 }>
        { inputControl }
      </Box>
    );
  }

  if (isTopLevel) {
    return (
      <ContractMethodFieldRow
        label={ <ContractMethodFieldLabel data={ data } isOptional={ isOptional } level={ level }/> }
      >
        { inputControl }
      </ContractMethodFieldRow>
    );
  }

  return (
    <Flex
      className={ className }
      flexDir={{ base: 'column', md: 'row' }}
      alignItems="flex-start"
      columnGap={ CONTRACT_METHOD_FIELD_ROW_GAP }
      w="100%"
      bgColor={ isNativeCoin ? styles.actionButtonHoverBg : undefined }
      borderRadius="base"
      border={ isNativeCoin ? '1px solid' : undefined }
      borderColor={ isNativeCoin ? styles.inputBorder : undefined }
      px={ px ?? '6px' }
      py={ isNativeCoin ? 1 : 0 }
    >
      <ContractMethodFieldLabel data={ data } isOptional={ isOptional } level={ level }/>
      { inputControl }
    </Flex>
  );
};

export default React.memo(chakra(ContractMethodFieldInput));
